import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  generationError,
  generationSuccess,
  resetImageState,
  setProgress,
  setSelectedStyle,
  setUploadedImage,
  startGeneration,
} from '../store/slices/imageSlice'
import { setStep } from '../store/slices/appSlice'
import ImageLightbox from '../components/ImageLightbox'
import {
  bathrobeOptions,
  beddingFabrics,
  getBeddingFabricById,
  getBeddingVariantsByFabric,
} from '../data/productCatalog'
import { sceneStyles } from '../data/styles'
import { generateStylizedImage } from '../services/api'
import generationVideoUrl from '../../assets/动画视频.mp4'
import generationVideoPosterUrl from '../../assets/动画视频-poster.jpg'

const generationRequests = new Map()
const presetImageModules = import.meta.glob('../../public/generated-images/**/*.jpeg', {
  eager: true,
  query: '?url',
  import: 'default',
})

const getPresetImageUrl = (folder, fileName) =>
  presetImageModules[`../../public/generated-images/${folder}/${fileName}`]

const presetImageConfig = {
  wave: {
    folder: '水波纹',
    colorMap: { 'wave-white': '白色', 'wave-grey': '浅灰', 'wave-cream': '浅米' },
  },
  'stripe-fine': {
    folder: '条纹1cm',
    colorMap: { 'stripe-fine-white': '白色', 'stripe-fine-grey': '浅灰', 'stripe-fine-cream': '浅米' },
  },
  'small-square': {
    folder: '小方格',
    colorMap: { 'small-square-white': '白色', 'small-square-grey': '浅灰', 'small-square-cream': '浅米' },
  },
  'window-grid': {
    folder: '回字格',
    colorMap: { 'window-grid-white': '白色', 'window-grid-grey': '浅灰', 'window-grid-cream': '浅米' },
  },
  sateen: {
    folder: '缎纹',
    colorMap: {
      'sateen-white': '白色',
      'sateen-grey': '灰色',
      'sateen-cream': '米色',
      'sateen-print-1': '印花1',
      'sateen-print-2': '印花2',
      'sateen-print-3': '印花3',
      'sateen-print-4': '印花4',
      'sateen-print-5': '印花5',
    },
  },
}

const sceneFileNameMap = {
  quick: '快捷',
  business: '商务',
  japanese: '日式',
  resort: '度假酒店',
}

const convertImageToDataUrl = async (imageUrl) => {
  const response = await fetch(imageUrl)
  const blob = await response.blob()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

const buildReferenceImages = ({ bedding, bathrobe }) => {
  const referenceImages = []

  if (bedding) {
    const usesDedicatedSizeReference =
      bedding.fabricId === 'window-grid' && bedding.sizeReferencePreview && bedding.sizeReferenceBase64

    if (bedding.sizeReferencePreview && bedding.sizeReferenceBase64) {
      referenceImages.push({
        id: 'bedding-size',
        role: 'bedding_size',
        labelZh: bedding.sizeReferenceNameZh || '纹样大小参考',
        labelEn: bedding.sizeReferenceNameEn || 'Pattern Scale Reference',
        preview: bedding.sizeReferencePreview,
        base64: bedding.sizeReferenceBase64,
      })
    }

    if (!usesDedicatedSizeReference) {
      referenceImages.push({
        id: 'bedding-variant',
        role: 'bedding_pattern',
        labelZh: '四件套印花/颜色参考',
        labelEn: 'Bedding Pattern/Color Reference',
        preview: bedding.variantReferencePreview || bedding.variantPreview,
        base64: bedding.variantReferenceBase64 || bedding.variantBase64,
      })
    }
  }

  if (bathrobe) {
    referenceImages.push({
      id: 'bathrobe-style',
      role: 'bathrobe_style',
      labelZh: '浴袍款式参考',
      labelEn: 'Bathrobe Style Reference',
      preview: bathrobe.optionPreview,
      base64: bathrobe.optionBase64,
    })
    referenceImages.push({
      id: 'bathrobe-material',
      role: 'bathrobe_material',
      labelZh: '浴袍面料参考',
      labelEn: 'Bathrobe Material Reference',
      preview: bathrobe.materialPreview,
      base64: bathrobe.materialBase64,
    })
  }

  return referenceImages
}

function Upload() {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const {
    uploadedImage,
    uploadedImagePreview,
    selectedSourceMeta,
    selectedStyle,
    generationRequestId,
    generateStatus,
    resultImage,
    errorMessage,
    resetVersion,
  } = useSelector((state) => state.image)
  const currentLanguage = i18n.resolvedLanguage || i18n.language

  const currentBedding = selectedSourceMeta?.bedding || null
  const currentBathrobe = selectedSourceMeta?.bathrobe || null
  const [activeFabricId, setActiveFabricId] = useState(currentBedding?.fabricId || beddingFabrics[0].id)
  const [previewFabric, setPreviewFabric] = useState(
    currentBedding?.fabricPreview
      ? {
        id: currentBedding.fabricId,
        nameZh: currentBedding.fabricNameZh,
        nameEn: currentBedding.fabricNameEn,
        nameJa: currentBedding.fabricNameJa,
        nameKr: currentBedding.fabricNameKr,
        preview: currentBedding.fabricPreview,
      }
      : null
  )
  const [beddingCatalogLevel, setBeddingCatalogLevel] = useState('fabric')
  const [pendingSelectionId, setPendingSelectionId] = useState(null)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [visualPanelHeight, setVisualPanelHeight] = useState(null)
  const [stepNotice, setStepNotice] = useState(null)
  const visualPanelRef = useRef(null)
  const fabricSectionRef = useRef(null)
  const variantSectionRef = useRef(null)
  const bathrobeSectionRef = useRef(null)
  const sceneSectionRef = useRef(null)
  const stepNoticeTimeoutRef = useRef(null)
  const bathrobeSelectionRequestRef = useRef(0)
  const bathrobeBase64CacheRef = useRef(new Map())
  const requestStartedRef = useRef(false)
  const presetImagePathRef = useRef(null)
  const lastSceneNumberRef = useRef(null)

  const activeFabric = getBeddingFabricById(activeFabricId) || beddingFabrics[0]
  const beddingVariants = getBeddingVariantsByFabric(activeFabric.id)
  const hasCompleteSelection = Boolean(
    uploadedImagePreview &&
      selectedStyle?.id &&
      currentBedding?.variantId &&
      currentBathrobe?.optionId
  )
  const hasBathrobeReferenceData = !currentBathrobe?.optionId || Boolean(
    currentBathrobe.optionBase64 &&
      currentBathrobe.materialBase64
  )
  const canGenerate = hasCompleteSelection && hasBathrobeReferenceData && !pendingSelectionId && generateStatus !== 'loading'

  const isSpecialCondition = Boolean(
    currentBathrobe?.optionId === 'bathrobe-terry' &&
      presetImageConfig[currentBedding?.fabricId] &&
      presetImageConfig[currentBedding?.fabricId].colorMap[currentBedding?.variantId]
  )

  useEffect(() => {
    dispatch(setStep(1))
  }, [dispatch])

  useEffect(() => {
    const videoPreload = document.createElement('link')
    videoPreload.rel = 'preload'
    videoPreload.as = 'video'
    videoPreload.href = generationVideoUrl
    videoPreload.type = 'video/mp4'

    const posterPreload = document.createElement('link')
    posterPreload.rel = 'preload'
    posterPreload.as = 'image'
    posterPreload.href = generationVideoPosterUrl

    const warmupVideo = document.createElement('video')
    warmupVideo.src = generationVideoUrl
    warmupVideo.poster = generationVideoPosterUrl
    warmupVideo.muted = true
    warmupVideo.loop = true
    warmupVideo.playsInline = true
    warmupVideo.preload = 'auto'
    warmupVideo.style.position = 'fixed'
    warmupVideo.style.left = '-2px'
    warmupVideo.style.top = '-2px'
    warmupVideo.style.width = '1px'
    warmupVideo.style.height = '1px'
    warmupVideo.style.opacity = '0'
    warmupVideo.style.pointerEvents = 'none'

    document.head.append(videoPreload, posterPreload)
    document.body.append(warmupVideo)
    warmupVideo.load()
    warmupVideo.play().catch(() => {})

    return () => {
      videoPreload.remove()
      posterPreload.remove()
      warmupVideo.remove()
    }
  }, [])

  useEffect(() => {
    if (currentBedding?.fabricId) {
      setActiveFabricId(currentBedding.fabricId)
      setPreviewFabric({
        id: currentBedding.fabricId,
        nameZh: currentBedding.fabricNameZh,
        nameEn: currentBedding.fabricNameEn,
        nameJa: currentBedding.fabricNameJa,
        nameKr: currentBedding.fabricNameKr,
        preview: currentBedding.fabricPreview,
      })
    }
  }, [
    currentBedding?.fabricId,
    currentBedding?.fabricNameEn,
    currentBedding?.fabricNameJa,
    currentBedding?.fabricNameKr,
    currentBedding?.fabricNameZh,
    currentBedding?.fabricPreview,
  ])

  useEffect(() => {
    setActiveFabricId(beddingFabrics[0].id)
    setPreviewFabric(null)
    setPendingSelectionId(null)
    setBeddingCatalogLevel('fabric')
    setStepNotice(null)
  }, [resetVersion])

  useEffect(() => () => {
    if (stepNoticeTimeoutRef.current) {
      window.clearTimeout(stepNoticeTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    const panel = visualPanelRef.current
    if (!panel) return undefined

    const updatePanelHeight = () => {
      const nextHeight = Math.round(panel.getBoundingClientRect().height)
      setVisualPanelHeight((currentHeight) => (
        Math.abs((currentHeight || 0) - nextHeight) > 1 ? nextHeight : currentHeight
      ))
    }

    updatePanelHeight()

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updatePanelHeight)
      : null

    resizeObserver?.observe(panel)
    window.addEventListener('resize', updatePanelHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updatePanelHeight)
    }
  }, [])

  useEffect(() => {
    if (generateStatus !== 'loading' || resultImage || requestStartedRef.current) {
      return undefined
    }

    if (!hasCompleteSelection || !hasBathrobeReferenceData) {
      dispatch(
        generationError({
          requestId: generationRequestId,
          message: t('studio.requirementsHint', { defaultValue: '请先完成面料、颜色/印花、浴袍与场景选择。' }),
        })
      )
      return undefined
    }

    requestStartedRef.current = true
    const requestId = generationRequestId
    let progressInterval = null
    let completeTimeout = null
    let didCancel = false

    if (isSpecialCondition) {
      if (!presetImagePathRef.current) {
        const config = presetImageConfig[currentBedding.fabricId]
        const colorName = config.colorMap[currentBedding.variantId]
        const sceneName = sceneFileNameMap[selectedStyle.id] || selectedStyle.id
        const lastSceneNumber = lastSceneNumberRef.current
        const sceneNumber = lastSceneNumber === 1 ? 2 : 1
        const fileName = `${config.folder}-${colorName}-毛圈白-${sceneName}-场景${sceneNumber}.jpeg`
        const path = getPresetImageUrl(config.folder, fileName)

        if (!path) {
          dispatch(
            generationError({
              requestId,
              message: `预设图片不存在：${config.folder}/${fileName}`,
            })
          )
          return undefined
        }

        lastSceneNumberRef.current = sceneNumber
        presetImagePathRef.current = path
      }

      let progressValue = 0
      progressInterval = window.setInterval(() => {
        progressValue += Math.random() * 15
        dispatch(setProgress(Math.min(progressValue, 95)))
      }, 1500)

      completeTimeout = window.setTimeout(() => {
        if (didCancel) return
        window.clearInterval(progressInterval)
        dispatch(setProgress(100))
        dispatch(
          generationSuccess({
            requestId,
            resultUrl: presetImagePathRef.current,
          })
        )
      }, 8000)

      return () => {
        didCancel = true
        window.clearInterval(progressInterval)
        window.clearTimeout(completeTimeout)
        requestStartedRef.current = false
      }
    }

    let request = generationRequests.get(requestId)

    if (!request) {
      request = generateStylizedImage({
        sourceImage: uploadedImage,
        style: selectedStyle,
        sourceMeta: selectedSourceMeta,
        requestId,
      }, (nextProgress) => {
        dispatch(setProgress(nextProgress))
      })

      generationRequests.set(requestId, request)
    }

    request
      .then((result) => {
        if (didCancel) return
        dispatch(
          generationSuccess({
            requestId,
            resultUrl: result.resultUrl,
          })
        )
      })
      .catch((error) => {
        if (didCancel) return
        console.error('Generation error:', error)
        dispatch(
          generationError({
            requestId,
            message: error.message || t('result.error.desc'),
          })
        )
      })
      .finally(() => {
        generationRequests.delete(requestId)
      })

    return () => {
      didCancel = true
      requestStartedRef.current = false
    }
  }, [
    currentBathrobe,
    currentBedding,
    dispatch,
    generateStatus,
    generationRequestId,
    hasBathrobeReferenceData,
    hasCompleteSelection,
    isSpecialCondition,
    resultImage,
    selectedSourceMeta,
    selectedStyle,
    t,
    uploadedImage,
  ])

  useEffect(() => {
    if (generateStatus !== 'loading') {
      requestStartedRef.current = false
    }
  }, [generateStatus])

  useEffect(() => {
    if (generateStatus !== 'loading') return undefined

    const keepLoadingVideoAlive = () => {
      document.querySelectorAll('video[data-generation-video="true"]').forEach((video) => {
        if (!(video instanceof HTMLVideoElement)) return
        video.loop = true

        if (video.ended) {
          video.currentTime = 0
        }

        if (video.paused || video.ended) {
          video.play().catch(() => {})
        }
      })
    }

    keepLoadingVideoAlive()
    const playbackCheck = window.setInterval(keepLoadingVideoAlive, 1000)

    return () => {
      window.clearInterval(playbackCheck)
    }
  }, [generateStatus])

  const getLocalizedName = (item) => {
    switch (currentLanguage) {
      case 'zh-CN':
        return item.nameZh || item.nameEn
      case 'ja-JP':
        return item.nameJa || item.nameEn
      case 'ko-KR':
        return item.nameKr || item.nameEn
      default:
        return item.nameEn || item.nameZh
    }
  }

  const formatPairLabel = (left, right) => [left, right].filter(Boolean).join(' / ')

  const persistSelection = ({ bedding = currentBedding, bathrobe = currentBathrobe, fallbackFabric = activeFabric }) => {
    const referenceImages = buildReferenceImages({ bedding, bathrobe })
    const primaryPreview = bedding?.variantPreview || bathrobe?.optionPreview || fallbackFabric.preview
    const primaryBase64 = bedding?.variantReferenceBase64 || bedding?.variantBase64 || bathrobe?.optionBase64 || null

    dispatch(
      setUploadedImage({
        base64: primaryBase64,
        preview: primaryPreview,
        meta: {
          productType: 'bundle',
          bedding,
          bathrobe,
          referenceImages,
        },
      })
    )
  }

  const showStepNotice = (message) => {
    if (stepNoticeTimeoutRef.current) {
      window.clearTimeout(stepNoticeTimeoutRef.current)
      stepNoticeTimeoutRef.current = null
    }

    setStepNotice(message)
    stepNoticeTimeoutRef.current = window.setTimeout(() => {
      setStepNotice(null)
      stepNoticeTimeoutRef.current = null
    }, 2500)
  }

  const scrollToCatalogStep = (step) => {
    const sectionRefMap = {
      fabric: fabricSectionRef,
      variant: variantSectionRef,
      bathrobe: bathrobeSectionRef,
      scene: sceneSectionRef,
    }
    const sectionRef = sectionRefMap[step]

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        sectionRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    })
  }

  const getMissingBeddingStep = () => {
    if (!previewFabric?.id && !currentBedding?.fabricId) return 'fabric'
    if (!currentBedding?.variantId) return 'variant'
    return null
  }

  const promptRequiredStep = (step) => {
    if (step === 'fabric') {
      setBeddingCatalogLevel('fabric')
      showStepNotice(t('studio.sequence.pickFabricFirst', { defaultValue: '请先选择面料。' }))
      scrollToCatalogStep('fabric')
      return
    }

    if (step === 'variant') {
      setBeddingCatalogLevel('variant')
      showStepNotice(t('studio.sequence.pickVariantFirst', { defaultValue: '请先选择颜色或印花。' }))
      scrollToCatalogStep('variant')
      return
    }

    if (step === 'bathrobe') {
      showStepNotice(t('studio.sequence.pickBathrobeFirst', { defaultValue: '请先选择浴袍面料。' }))
      scrollToCatalogStep('bathrobe')
      return
    }

    showStepNotice(t('studio.sequence.pickSceneFirst', { defaultValue: '请先选择场景。' }))
    scrollToCatalogStep('scene')
  }

  const ensureBeddingSelection = () => {
    const missingStep = getMissingBeddingStep()
    if (!missingStep) return true

    promptRequiredStep(missingStep)
    return false
  }

  const buildBathrobeSelection = (option, base64 = {}) => ({
    optionId: option.id,
    optionNameZh: option.nameZh,
    optionNameEn: option.nameEn,
    optionNameJa: option.nameJa || option.nameEn,
    optionNameKr: option.nameKr || option.nameEn,
    optionPreview: option.preview,
    optionBase64: base64.optionBase64 || null,
    materialPreview: option.swatch,
    materialBase64: base64.materialBase64 || null,
  })

  const hydrateBathrobeBase64 = async (option) => {
    const cachedBase64 = bathrobeBase64CacheRef.current.get(option.id)
    if (cachedBase64) return cachedBase64

    const [optionBase64, materialBase64] = await Promise.all([
      convertImageToDataUrl(option.preview),
      convertImageToDataUrl(option.swatch),
    ])
    const nextBase64 = { optionBase64, materialBase64 }
    bathrobeBase64CacheRef.current.set(option.id, nextBase64)
    return nextBase64
  }

  const handleSelectFabric = (fabric) => {
    bathrobeSelectionRequestRef.current += 1
    setActiveFabricId(fabric.id)
    setPreviewFabric(fabric)
    setBeddingCatalogLevel('variant')

    if (currentBedding?.fabricId && currentBedding.fabricId !== fabric.id) {
      persistSelection({ bedding: null, bathrobe: currentBathrobe, fallbackFabric: fabric })
    }
  }

  const handleSelectVariant = async (variant) => {
    bathrobeSelectionRequestRef.current += 1
    setPendingSelectionId(variant.id)

    try {
      const selectedFabricPreview = activeFabric.preview
      const selectedVariantPreview = variant.preview
      const selectedVariantReferencePreview =
        variant.modelReferencePreview || activeFabric.modelReferencePreview || selectedVariantPreview
      const selectedSizeReferencePreview = variant.sizeReferencePreview || activeFabric.sizeReferencePreview || null
      const selectedVariantPromptNameZh = variant.promptNameZh || variant.nameZh
      const selectedVariantPromptNameEn = variant.promptNameEn || variant.nameEn

      const variantBase64Promise = convertImageToDataUrl(selectedVariantPreview)
      const variantReferenceBase64Promise =
        selectedVariantReferencePreview === selectedVariantPreview
          ? variantBase64Promise
          : convertImageToDataUrl(selectedVariantReferencePreview)

      const [fabricBase64, variantBase64, variantReferenceBase64, sizeReferenceBase64] = await Promise.all([
        convertImageToDataUrl(selectedFabricPreview),
        variantBase64Promise,
        variantReferenceBase64Promise,
        selectedSizeReferencePreview ? convertImageToDataUrl(selectedSizeReferencePreview) : Promise.resolve(null),
      ])

      const bedding = {
        fabricId: activeFabric.id,
        fabricNameZh: activeFabric.nameZh,
        fabricNameEn: activeFabric.nameEn,
        fabricNameJa: activeFabric.nameJa || activeFabric.nameEn,
        fabricNameKr: activeFabric.nameKr || activeFabric.nameEn,
        fabricPreview: selectedFabricPreview,
        fabricSpecHintZh: activeFabric.sizeHintZh || '',
        fabricSpecHintEn: activeFabric.sizeHintEn || '',
        fabricBase64,
        variantId: variant.id,
        variantNameZh: variant.nameZh,
        variantNameEn: variant.nameEn,
        variantNameJa: variant.nameJa || variant.nameEn,
        variantNameKr: variant.nameKr || variant.nameEn,
        variantPromptNameZh: selectedVariantPromptNameZh,
        variantPromptNameEn: selectedVariantPromptNameEn,
        variantPromptNameJa: variant.promptNameJa || selectedVariantPromptNameEn,
        variantPromptNameKr: variant.promptNameKr || selectedVariantPromptNameEn,
        variantPreview: selectedVariantPreview,
        variantBase64,
        variantReferencePreview: selectedVariantReferencePreview,
        variantReferenceBase64,
        sizeReferencePreview: selectedSizeReferencePreview,
        sizeReferenceNameZh: variant.sizeReferenceNameZh || activeFabric.sizeReferenceNameZh || '',
        sizeReferenceNameEn: variant.sizeReferenceNameEn || activeFabric.sizeReferenceNameEn || '',
        sizeReferenceBase64,
      }

      setPreviewFabric(activeFabric)
      persistSelection({ bedding, bathrobe: currentBathrobe })
    } catch (error) {
      console.error('Select bedding failed:', error)
    } finally {
      setPendingSelectionId(null)
    }
  }

  const handleSelectBathrobe = async (option) => {
    if (!ensureBeddingSelection()) return

    const requestId = bathrobeSelectionRequestRef.current + 1
    bathrobeSelectionRequestRef.current = requestId
    const optimisticBathrobe = buildBathrobeSelection(option)

    try {
      persistSelection({ bedding: currentBedding, bathrobe: optimisticBathrobe })

      const base64 = await hydrateBathrobeBase64(option)
      if (bathrobeSelectionRequestRef.current !== requestId) return

      persistSelection({
        bedding: currentBedding,
        bathrobe: buildBathrobeSelection(option, base64),
      })
    } catch (error) {
      console.error('Select bathrobe failed:', error)
    }
  }

  const handleSceneSelect = (scene) => {
    const missingBeddingStep = getMissingBeddingStep()
    if (missingBeddingStep) {
      promptRequiredStep(missingBeddingStep)
      return
    }

    if (!currentBathrobe?.optionId) {
      promptRequiredStep('bathrobe')
      return
    }

    presetImagePathRef.current = null
    lastSceneNumberRef.current = null
    dispatch(setSelectedStyle(scene))
  }

  const handleGenerate = () => {
    if (!canGenerate) {
      const missingBeddingStep = getMissingBeddingStep()
      if (missingBeddingStep) {
        promptRequiredStep(missingBeddingStep)
      } else if (!currentBathrobe?.optionId || !hasBathrobeReferenceData) {
        promptRequiredStep('bathrobe')
      } else if (!selectedStyle?.id) {
        promptRequiredStep('scene')
      }
      return
    }

    presetImagePathRef.current = null
    requestStartedRef.current = false
    dispatch(startGeneration())
  }

  const handleRegenerate = () => {
    if (!hasCompleteSelection || !hasBathrobeReferenceData || generateStatus === 'loading') return
    presetImagePathRef.current = null
    requestStartedRef.current = false
    dispatch(startGeneration())
  }

  const handleReset = () => {
    presetImagePathRef.current = null
    lastSceneNumberRef.current = null
    requestStartedRef.current = false
    setActiveFabricId(beddingFabrics[0].id)
    setPreviewFabric(null)
    setBeddingCatalogLevel('fabric')
    dispatch(resetImageState())
  }

  const triggerDownload = ({ href, fileName, openInNewTab = false }) => {
    const anchor = document.createElement('a')
    anchor.href = href

    if (openInNewTab) {
      anchor.target = '_blank'
      anchor.rel = 'noopener noreferrer'
    } else {
      anchor.download = fileName
    }

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }

  const handleDownload = async () => {
    if (!resultImage) return

    setIsDownloading(true)
    const fileName = `hotel-linen-${Date.now()}.png`

    try {
      const response = await fetch(resultImage)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      triggerDownload({ href: url, fileName })

      window.setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, 1000)
    } catch (error) {
      console.error('Download failed:', error)
      triggerDownload({ href: resultImage, fileName, openInNewTab: true })
    } finally {
      setIsDownloading(false)
    }
  }

  const getStyleName = () => {
    if (!selectedStyle) return ''
    return t(`styleSelect.scenes.${selectedStyle.id}.title`, {
      defaultValue: selectedStyle.nameZh || selectedStyle.name || '',
    })
  }

  const getStyleDescription = () => {
    if (!selectedStyle) return ''
    return t(`styleSelect.scenes.${selectedStyle.id}.description`, {
      defaultValue: selectedStyle.descriptionZh || selectedStyle.description || '',
    })
  }

  const beddingReferenceLabel = currentBedding?.variantId
    ? formatPairLabel(
      getLocalizedName({
        nameZh: currentBedding.fabricNameZh,
        nameEn: currentBedding.fabricNameEn,
        nameJa: currentBedding.fabricNameJa,
        nameKr: currentBedding.fabricNameKr,
      }),
      getLocalizedName({
        nameZh: currentBedding.variantNameZh,
        nameEn: currentBedding.variantNameEn,
        nameJa: currentBedding.variantNameJa,
        nameKr: currentBedding.variantNameKr,
      })
    )
    : ''
  const bathrobeReferenceLabel = currentBathrobe?.optionId
    ? getLocalizedName({
      nameZh: currentBathrobe.optionNameZh,
      nameEn: currentBathrobe.optionNameEn,
      nameJa: currentBathrobe.optionNameJa,
      nameKr: currentBathrobe.optionNameKr,
    })
    : ''
  const sourceName = [beddingReferenceLabel, bathrobeReferenceLabel].filter(Boolean).join(' / ')
  const zoomHint = t('common.zoomHint')
  const closePreviewLabel = t('common.closePreview')

  const previewSlots = useMemo(() => {
    const emptyTitle = t('studio.emptySlot', { defaultValue: '待选择，请在右侧产品库选择' })
    const fabricTitle = previewFabric ? getLocalizedName(previewFabric) : emptyTitle

    return [
      {
        id: 'fabric',
        type: t('studio.thumbnail.fabric', { defaultValue: '面料' }),
        title: fabricTitle,
        src: previewFabric?.preview || null,
        placeholderSrc: beddingFabrics[0]?.preview,
        caption: t('upload.sections.fabricTitle'),
      },
      {
        id: 'bedding',
        type: t('studio.thumbnail.bedding', { defaultValue: '四件套' }),
        title: currentBedding?.variantPreview ? beddingReferenceLabel : emptyTitle,
        src: currentBedding?.variantPreview || null,
        placeholderSrc: beddingFabrics[1]?.preview || beddingFabrics[0]?.preview,
        caption: t('upload.sections.variantTitle'),
      },
      {
        id: 'bathrobe',
        type: t('studio.thumbnail.bathrobe', { defaultValue: '浴袍' }),
        title: currentBathrobe?.materialPreview ? bathrobeReferenceLabel : emptyTitle,
        src: currentBathrobe?.materialPreview || null,
        placeholderSrc: bathrobeOptions[0]?.swatch,
        caption: t('upload.sections.bathrobeTitle'),
      },
      {
        id: 'scene',
        type: t('studio.thumbnail.scene', { defaultValue: '场景' }),
        title: selectedStyle?.thumbnail ? getStyleName() : emptyTitle,
        src: selectedStyle?.thumbnail || null,
        placeholderSrc: sceneStyles[0]?.thumbnail,
        caption: t('studio.sceneThumbCaption', { defaultValue: '场景参考图' }),
      },
    ]
  }, [
    bathrobeReferenceLabel,
    beddingReferenceLabel,
    currentBathrobe,
    currentBedding,
    currentLanguage,
    previewFabric,
    selectedStyle,
    t,
  ])

  const selectionThumbnails = useMemo(() => previewSlots.filter((item) => item.src), [previewSlots])
  const studioCompletionCount = selectionThumbnails.length

  const openLightbox = ({ src, alt, title, caption }) => {
    if (!src) return
    setLightboxImage({ src, alt, title, caption })
  }

  const replayGenerationVideo = (video, shouldRestart = false) => {
    if (!video) return

    video.loop = true

    if (shouldRestart || video.ended) {
      video.currentTime = 0
    }

    video.play().catch(() => {})
  }

  const handleGenerationVideoReady = (event) => {
    replayGenerationVideo(event.currentTarget)
  }

  const handleGenerationVideoEnded = (event) => {
    replayGenerationVideo(event.currentTarget, true)
  }

  const handleGenerationVideoInterrupted = (event) => {
    replayGenerationVideo(event.currentTarget)
  }

  const handleGenerationVideoTimeUpdate = (event) => {
    const video = event.currentTarget

    if (
      Number.isFinite(video.duration) &&
      video.duration > 0 &&
      video.currentTime >= video.duration - 0.12
    ) {
      replayGenerationVideo(video, true)
    }
  }

  const renderGenerateActions = () => (
    <div className="shrink-0 overflow-hidden rounded-[16px] border border-[#d8c9aa]/58 bg-[#f6f0e6]/88 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)] backdrop-blur-md">
      <div className="flex items-center justify-between border-b border-[#d8c9aa]/50 px-4 py-2.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8d7a5d]">
          {t('studio.renderQueue', { defaultValue: 'Render Queue' })}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#5c5145]">
          {studioCompletionCount}/4
        </span>
      </div>
      <div className="flex flex-col gap-px sm:flex-row">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className={`inline-flex flex-1 items-center justify-center gap-3 px-6 py-4 text-[15px] font-bold tracking-[0.04em] transition-all ${
            canGenerate
              ? 'bg-[linear-gradient(135deg,#5f5a52_0%,#7a7166_54%,#958a7d_100%)] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] hover:brightness-105 active:scale-[0.99]'
              : 'cursor-not-allowed bg-[#ded6ca] text-[#746b61]/70'
          }`}
        >
          {generateStatus === 'loading'
            ? t('studio.buttons.generating', { defaultValue: '生成中...' })
            : t('studio.buttons.generate', { defaultValue: '生成效果图' })}
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]" data-icon="auto_awesome" />
        </button>
        {resultImage && (
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={generateStatus === 'loading'}
            className="inline-flex items-center justify-center gap-2 border-l border-[#d8c9aa]/55 bg-[#e7dfd2] px-5 py-4 text-[13px] font-semibold tracking-[0.1em] text-[#5c5145] transition-all duration-200 hover:bg-[#ded6ca] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]" data-icon="refresh" />
            {t('result.buttons.regenerate')}
          </button>
        )}
      </div>
    </div>
  )

  const renderSelectedThumbnails = () => {
    if (selectionThumbnails.length === 0) return null

    return (
      <section className="overflow-hidden rounded-3xl bg-[linear-gradient(135deg,rgba(255,255,255,0.86),rgba(216,201,170,0.58))] p-px font-bold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)] backdrop-blur-md xl:shrink-0">
        <div className="overflow-hidden rounded-[23px] bg-[#f7f3ec]/90">
          <div className="overflow-hidden rounded-[23px] px-5 pb-4 pt-5">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#8d7a5d]">
              {t('studio.selectedThumbs', { defaultValue: 'Selected Thumbnails' })}
            </p>
            <h2 className="mt-1 font-headline text-[1.28rem] font-bold leading-tight text-[#211b14]">
              {hasCompleteSelection
                ? t('studio.readyHint', { defaultValue: 'Selections are complete. Ready to generate.' })
                : t('studio.requirementsHint', { defaultValue: 'Choose fabric, color / print, bathrobe, and scene first.' })}
            </h2>
          </div>

          <div className="selected-thumbnail-grid grid auto-rows-[7.4rem] grid-cols-2 items-stretch gap-3 px-5 pb-5 sm:grid-cols-4 xl:grid-cols-4">
            {previewSlots.map((item) => {
              const hasImage = Boolean(item.src)
              const cardBody = (
                <>
                  <div className="relative h-[4.45rem] w-full shrink-0 overflow-hidden bg-[#ebe5da]">
                    {(item.src || item.placeholderSrc) && (
                      <img
                        src={item.src || item.placeholderSrc}
                        alt={item.title}
                        className={`h-full w-full object-cover transition-transform duration-500 ${
                          hasImage ? 'group-hover:scale-105' : 'opacity-28 grayscale'
                        }`}
                      />
                    )}
                  </div>
                  <div className="flex min-h-0 flex-1 items-center bg-[#e9e1d6]/90 px-3 py-2">
                    <p className={`line-clamp-2 text-[12px] font-bold leading-[1.35] ${hasImage ? 'text-[#2b2722]' : 'text-[#8f877b]'}`}>
                      {hasImage ? item.title : t('studio.thumbnailPending', { defaultValue: 'Pending' })}
                    </p>
                  </div>
                </>
              )

              if (!hasImage) {
                return (
                  <div
                    key={item.id}
                    className="flex h-full flex-col overflow-hidden rounded-[13px] border border-[#d8c9aa]/45 bg-[#e9e1d6]/80"
                  >
                    {cardBody}
                  </div>
                )
              }

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openLightbox({
                    src: item.src,
                    alt: item.title,
                  title: item.title,
                  caption: item.caption,
                })}
                  className="group flex h-full flex-col overflow-hidden rounded-[13px] border border-[#d8c9aa]/45 bg-[#e9e1d6]/80 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-[#b9934f]/70"
                  aria-label={`${zoomHint}: ${item.title}`}
                >
                  {cardBody}
                </button>
              )
            })}
          </div>
        </div>
      </section>
    )
  }

  const renderCatalogSectionHeader = ({ index, title, action = null }) => (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[#d8c9aa]/70 bg-[#f7f1e7]/78 font-mono text-[11px] font-bold tracking-[0.08em] text-[#8d7a5d] shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]">
            {index}
          </span>
          <div className="min-w-0 border-l border-[#d8c9aa]/62 pl-3">
            <h3 className="truncate font-serif text-[1.15rem] font-bold leading-tight tracking-normal text-[#211b14]">
              {title}
            </h3>
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div
        className="h-px w-[min(72%,20rem)] bg-[linear-gradient(90deg,rgba(185,147,79,0.44)_0%,rgba(216,201,170,0.24)_62%,rgba(216,201,170,0)_100%)]"
        aria-hidden="true"
      />
    </div>
  )

  const renderStepNoticeToast = () => {
    if (!stepNotice) return null

    return (
      <div className="pointer-events-none fixed inset-x-0 top-[6.2rem] z-[80] flex justify-center px-4 md:top-[6.8rem]">
        <div
          role="status"
          aria-live="polite"
          className="flex max-w-[22rem] items-center gap-3 rounded-full border border-white/24 bg-[rgba(95,88,79,0.96)] px-4 py-3 shadow-[0_16px_46px_rgba(35,29,20,0.28)] backdrop-blur-md"
        >
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#f0dfae] text-[#2a2011]">
            <span aria-hidden="true" className="material-symbols-outlined text-[17px]" style={{ fontVariationSettings: "'FILL' 1" }} data-icon="priority_high" />
          </span>
          <p className="font-serif text-[0.98rem] font-bold leading-snug text-[#fff7df] drop-shadow-[0_1px_5px_rgba(0,0,0,0.35)]">
            {stepNotice}
          </p>
        </div>
      </div>
    )
  }

  const renderResultContent = () => {
    if (generateStatus === 'loading') {
      return (
        <div
          className="generation-video-stage h-full min-h-[34rem]"
          style={{ backgroundImage: `url(${generationVideoPosterUrl})` }}
        >
          <video
            key={generationRequestId}
            data-generation-video="true"
            className="generation-video-media"
            src={generationVideoUrl}
            poster={generationVideoPosterUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
            onCanPlay={handleGenerationVideoReady}
            onEnded={handleGenerationVideoEnded}
            onPause={handleGenerationVideoInterrupted}
            onStalled={handleGenerationVideoInterrupted}
            onWaiting={handleGenerationVideoInterrupted}
            onTimeUpdate={handleGenerationVideoTimeUpdate}
            aria-hidden="true"
          />
          <div className="generation-video-shade" />
        </div>
      )
    }

    if (generateStatus === 'success' && resultImage) {
      return (
        <button
          type="button"
          onClick={() => openLightbox({
            src: resultImage,
            alt: `${getStyleName()} ${sourceName}`.trim(),
            title: getStyleName() || t('result.title'),
            caption: sourceName,
          })}
          className="relative block h-full min-h-[34rem] w-full cursor-zoom-in overflow-hidden rounded-[14px] bg-[#f7f3ea]/40"
          aria-label={`${zoomHint}${getStyleName() ? `: ${getStyleName()}` : ''}`}
        >
          <img
            alt={`${getStyleName()} ${sourceName}`.trim()}
            className="h-full w-full object-contain"
            src={resultImage}
          />
          <div
            className="generation-video-dissolve"
            style={{ backgroundImage: `url(${generationVideoPosterUrl})` }}
            aria-hidden="true"
          >
            <video
              key={`dissolve-${generationRequestId}`}
              data-generation-video="true"
              className="generation-video-media"
              src={generationVideoUrl}
              poster={generationVideoPosterUrl}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              disablePictureInPicture
              onCanPlay={handleGenerationVideoReady}
              onEnded={handleGenerationVideoEnded}
              onPause={handleGenerationVideoInterrupted}
              onStalled={handleGenerationVideoInterrupted}
              onWaiting={handleGenerationVideoInterrupted}
              onTimeUpdate={handleGenerationVideoTimeUpdate}
            />
          </div>
        </button>
      )
    }

    if (generateStatus === 'error') {
      return (
        <div className="flex h-full min-h-[34rem] flex-col items-center justify-center px-8 text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-error-container">
            <span aria-hidden="true" className="material-symbols-outlined text-4xl text-error" data-icon="error" />
          </div>
          <h2 className="mb-4 font-headline text-[2.2rem]">{t('result.error.title')}</h2>
          <p className="mb-2 max-w-md text-base text-on-surface-variant">{t('result.error.desc')}</p>
          {errorMessage && <p className="mb-8 max-w-md text-base text-error">{errorMessage}</p>}
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={!hasCompleteSelection || !hasBathrobeReferenceData}
            className="inline-flex items-center gap-2 bg-[#988f85] px-8 py-3 text-white transition-all duration-200 hover:bg-[#867d73] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]" data-icon="refresh" />
            {t('result.error.retry')}
          </button>
        </div>
      )
    }

    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[18px] bg-[#f7f3ea]/88 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.30)]">
        <div className="flex items-center justify-between border-b border-white/40 bg-[linear-gradient(135deg,#b0a79d_0%,#9a9187_58%,#867d73_100%)] px-5 py-3 text-white">
          <div>
            <h2 className="font-headline text-[1.2rem] leading-none tracking-[0.04em]">
              {t('studio.referenceStage', { defaultValue: 'Reference Stage' })}
            </h2>
          </div>
          <span className="rounded-full border border-white/36 bg-white/8 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/82">
            {studioCompletionCount}/4
          </span>
        </div>
        <div className="grid flex-1 grid-cols-2 grid-rows-[repeat(2,minmax(12rem,1fr))] gap-3 bg-[#f7f3ea]/82 p-3 sm:grid-rows-[repeat(2,minmax(15rem,1fr))] xl:min-h-0 xl:grid-rows-2">
          {previewSlots.map((item, index) => {
            if (item.src) {
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openLightbox({
                    src: item.src,
                    alt: item.title,
                    title: item.title,
                    caption: item.caption,
                  })}
                  className="group relative h-full min-h-0 overflow-hidden rounded-[12px] border border-[#d8c9aa]/55 bg-surface-container-low text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-[#b9934f]/80"
                  aria-label={`${zoomHint}: ${item.title}`}
                >
                  <img
                    src={item.src}
                    alt={item.title}
                    className="absolute inset-0 h-full w-full object-cover opacity-78 grayscale-[0.04] transition-all duration-500 group-hover:scale-105 group-hover:opacity-95"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(17,24,39,0.02)_0%,rgba(17,24,39,0.12)_48%,rgba(17,24,39,0.72)_100%)]" />
                  <span className="absolute left-4 top-4 rounded-full border border-white/36 bg-[#8f867d]/52 px-2 py-1 font-mono text-[10px] tracking-[0.12em] text-white/92 backdrop-blur">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-white drop-shadow-[0_1px_8px_rgba(0,0,0,0.45)]">{item.type}</p>
                    <p className="mt-1 line-clamp-2 text-[1rem] font-medium leading-snug text-white">{item.title}</p>
                  </div>
                </button>
              )
            }

            return (
              <div
                key={item.id}
                className="relative flex h-full min-h-0 overflow-hidden rounded-[12px] border border-[#d8c9aa]/45 bg-surface-container-low p-4 text-left shadow-[inset_0_0_0_1px_rgba(255,255,255,0.38)]"
                aria-label={item.type}
              >
                {item.placeholderSrc && (
                  <img
                    src={item.placeholderSrc}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover opacity-[0.38] grayscale-[0.22]"
                    aria-hidden="true"
                  />
                )}
                <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.68)_0%,rgba(245,239,230,0.42)_46%,rgba(212,200,178,0.32)_100%)]" />
                <div className="relative flex h-full w-full flex-col justify-between rounded-[9px] border border-dashed border-[#a99573]/34 bg-white/24 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#8d7a5d]">
                        {String(index + 1).padStart(2, '0')}
                      </p>
                      <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-on-surface-variant/70">{item.type}</p>
                    </div>
                    <span aria-hidden="true" className="material-symbols-outlined text-[20px] text-[#8d7a5d]/50" data-icon="image" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#d8d6d0]">
      <div
        className="pointer-events-none fixed inset-0 bg-cover bg-center opacity-[0.32] grayscale"
        style={{ backgroundImage: `url(${generationVideoPosterUrl})` }}
        aria-hidden="true"
      />
      <div className="pointer-events-none fixed inset-0 bg-[linear-gradient(180deg,rgba(54,54,51,0.36)_0%,rgba(211,207,198,0.92)_20%,rgba(239,237,231,0.98)_58%,rgba(226,222,213,0.98)_100%)]" aria-hidden="true" />
      <main className="upload-shell relative mx-auto max-w-[1680px] px-4 pb-12 pt-24 sm:px-6 md:px-8 xl:px-12 xl:pb-8 2xl:px-14">
        <div className="upload-workspace grid grid-cols-1 gap-8 rounded-[22px] xl:grid-cols-[minmax(0,1fr)_minmax(400px,500px)] xl:items-start xl:gap-10">
          <section
            ref={visualPanelRef}
            className="upload-visual-panel order-1 flex min-h-[40rem] flex-col gap-3 overflow-hidden rounded-[22px] border border-[#d8c9aa]/65 bg-[#eee8dd]/72 p-3 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)] backdrop-blur-md xl:min-h-0"
          >
            <div className="upload-stage-frame min-h-0 flex-1 overflow-hidden rounded-[18px]">
              {renderResultContent()}
            </div>
            {renderGenerateActions()}
          </section>

          <aside
            className="upload-catalog-column order-2 space-y-5 xl:flex xl:h-[var(--upload-panel-height)] xl:flex-col xl:gap-5 xl:space-y-0 xl:overflow-hidden xl:pr-2"
            style={{ '--upload-panel-height': visualPanelHeight ? `${visualPanelHeight}px` : 'clamp(38rem, calc(100dvh - 8rem), 64rem)' }}
          >
            {renderSelectedThumbnails()}

            <section className="product-catalog-panel rounded-[16px] border border-[#d8c9aa]/58 bg-[#f6f0e6]/88 p-4 font-bold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)] backdrop-blur-md md:p-5 xl:min-h-0 xl:flex-1 xl:overflow-y-auto">
              <header className="mb-5 flex items-center justify-between gap-4 border-b border-[#d8c9aa]/50 pb-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8d7a5d]">
                    {t('nav.productCatalog')}
                  </p>
                  <h2 className="mt-1 font-headline text-[1.28rem] font-bold leading-tight text-[#211b14]">
                    {t('studio.selectionControls', { defaultValue: 'Selection Controls' })}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex items-center gap-2 rounded-full border border-[#cbb790]/70 bg-white/54 px-4 py-2 text-[11px] font-bold tracking-[0.1em] text-[#211b14] transition-all duration-200 hover:border-[#9c7430] hover:bg-white active:scale-95"
                >
                  <span aria-hidden="true" className="material-symbols-outlined text-[17px]" data-icon="restart_alt" />
                  {t('studio.buttons.reset', { defaultValue: '清空选择' })}
                </button>
              </header>

              <div className="space-y-6">
                {beddingCatalogLevel === 'fabric' ? (
                  <section ref={fabricSectionRef} className="space-y-3">
                    {renderCatalogSectionHeader({
                      index: '1',
                      title: t('upload.sections.fabricTitle'),
                    })}

                    <div className="product-option-grid grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3">
                      {beddingFabrics.map((fabric) => {
                        const isActive = previewFabric?.id === fabric.id

                        return (
                          <button
                            key={fabric.id}
                            type="button"
                            onClick={() => handleSelectFabric(fabric)}
                            className={`group relative overflow-hidden rounded-[12px] border bg-white text-left transition-all duration-300 ${
                              isActive
                                ? 'border-[#b9934f] ring-1 ring-inset ring-[#b9934f]/25'
                                : 'border-[#d8c9aa]/55 hover:-translate-y-0.5 hover:border-[#b9934f]/70'
                            }`}
                          >
                            <div className="relative aspect-[1.08] overflow-hidden bg-surface-container-high">
                              <img
                                src={fabric.preview}
                                alt={getLocalizedName(fabric)}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.08]"
                              />
                              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(17,24,39,0.02)_0%,rgba(17,24,39,0.10)_40%,rgba(17,24,39,0.74)_100%)]" />
                            </div>
                            <div className="absolute inset-0 flex flex-col justify-between p-2.5">
                              <div className="flex justify-end">
                                {isActive && (
                                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#f0dfae] text-[#2a2011] shadow-[0_8px_20px_rgba(0,0,0,0.18)]">
                                    <span aria-hidden="true" className="material-symbols-outlined text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }} data-icon="check" />
                                  </span>
                                )}
                              </div>
                              <div>
                                <p className="text-[12px] font-bold leading-snug text-white drop-shadow-[0_1px_8px_rgba(0,0,0,0.42)]">
                                  {getLocalizedName(fabric)}
                                </p>
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </section>
                ) : (
                  <section ref={variantSectionRef} className="space-y-3">
                    {renderCatalogSectionHeader({
                      index: '1.1',
                      title: t('upload.sections.variantTitle'),
                      action: (
                        <button
                          type="button"
                          onClick={() => setBeddingCatalogLevel('fabric')}
                          className="inline-flex shrink-0 items-center gap-2 border border-outline-variant/25 bg-white/70 px-3 py-2 text-[11px] font-bold tracking-[0.1em] text-on-surface transition-all duration-200 hover:border-primary/40 active:scale-95"
                        >
                          <span aria-hidden="true" className="material-symbols-outlined text-[17px]" data-icon="arrow_back" />
                          {t('upload.buttons.back')}
                        </button>
                      ),
                    })}

                    <div className="product-option-grid grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3">
                      {beddingVariants.map((variant) => {
                        const isSelected = currentBedding?.variantId === variant.id
                        const isPending = pendingSelectionId === variant.id
                        const isActive = isSelected || isPending

                        return (
                          <button
                            key={variant.id}
                            type="button"
                            onClick={() => handleSelectVariant(variant)}
                            className={`group relative overflow-hidden rounded-[12px] border bg-white text-left transition-all duration-300 ${
                              isActive
                                ? 'border-[#b9934f] ring-1 ring-inset ring-[#b9934f]/25'
                                : 'border-[#d8c9aa]/55 hover:-translate-y-0.5 hover:border-[#b9934f]/70'
                            }`}
                          >
                            <div className="relative aspect-[1.08] overflow-hidden bg-surface-container-high">
                              <img
                                src={variant.preview}
                                alt={getLocalizedName(variant)}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.08]"
                              />
                              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(17,24,39,0.02)_0%,rgba(17,24,39,0.10)_40%,rgba(17,24,39,0.74)_100%)]" />
                            </div>
                            <div className="absolute inset-0 flex flex-col justify-between p-2.5">
                              <div className="flex justify-end">
                                {isPending ? (
                                  <span className="inline-flex min-h-7 items-center justify-center rounded-full bg-[#f0dfae] px-2.5 text-[10px] font-mono uppercase tracking-[0.08em] text-[#2a2011] shadow-[0_8px_20px_rgba(0,0,0,0.18)]">
                                    {t('upload.summary.selecting')}
                                  </span>
                                ) : isSelected ? (
                                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#f0dfae] text-[#2a2011] shadow-[0_8px_20px_rgba(0,0,0,0.18)]">
                                    <span aria-hidden="true" className="material-symbols-outlined text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }} data-icon="check" />
                                  </span>
                                ) : null}
                              </div>
                              <div>
                                <p className="text-[12px] font-bold leading-snug text-white drop-shadow-[0_1px_8px_rgba(0,0,0,0.42)]">
                                  {getLocalizedName(variant)}
                                </p>
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </section>
                )}

                <section ref={bathrobeSectionRef} className="space-y-3">
                  {renderCatalogSectionHeader({
                    index: '2',
                    title: t('upload.sections.bathrobeTitle'),
                  })}

                  <div className="product-option-grid grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3">
                    {bathrobeOptions.map((option) => {
                      const isSelected = currentBathrobe?.optionId === option.id
                      const isPending = pendingSelectionId === option.id
                      const isActive = isSelected || isPending

                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => handleSelectBathrobe(option)}
                          className={`group relative overflow-hidden rounded-[12px] border bg-white text-left transition-all duration-300 ${
                            isActive
                              ? 'border-[#b9934f] ring-1 ring-inset ring-[#b9934f]/25'
                              : 'border-[#d8c9aa]/55 hover:-translate-y-0.5 hover:border-[#b9934f]/70'
                          }`}
                        >
                          <div className="relative aspect-[1.08] overflow-hidden bg-surface-container-high">
                            <img
                              src={option.swatch}
                              alt={getLocalizedName(option)}
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.08]"
                            />
                            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(17,24,39,0.02)_0%,rgba(17,24,39,0.08)_40%,rgba(17,24,39,0.70)_100%)]" />
                          </div>
                          <div className="absolute inset-0 flex flex-col justify-between p-2.5">
                            <div className="flex justify-end">
                              {isPending ? (
                                <span className="inline-flex min-h-7 items-center justify-center rounded-full bg-[#f0dfae] px-2.5 text-[10px] font-mono uppercase tracking-[0.08em] text-[#2a2011] shadow-[0_8px_20px_rgba(0,0,0,0.18)]">
                                  {t('upload.summary.selecting')}
                                </span>
                              ) : isSelected ? (
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#f0dfae] text-[#2a2011] shadow-[0_8px_20px_rgba(0,0,0,0.18)]">
                                  <span aria-hidden="true" className="material-symbols-outlined text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }} data-icon="check" />
                                </span>
                              ) : null}
                            </div>
                            <div>
                              <p className="text-[12px] font-bold leading-snug text-white">{getLocalizedName(option)}</p>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </section>

                <section ref={sceneSectionRef} className="space-y-3">
                  {renderCatalogSectionHeader({
                    index: '3',
                    title: t('styleSelect.sceneTitle'),
                  })}

                  <div className="scene-option-grid grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {sceneStyles.map((scene) => {
                      const isSelected = selectedStyle?.id === scene.id
                      const sceneTitle = t(`styleSelect.scenes.${scene.id}.title`, {
                        defaultValue: scene.nameZh || scene.name,
                      })

                      return (
                        <button
                          key={scene.id}
                          type="button"
                          onClick={() => handleSceneSelect(scene)}
                          className={`group overflow-hidden rounded-[14px] border p-2.5 text-left transition-all duration-300 ${
                            isSelected
                              ? 'border-[#b9934f] bg-[linear-gradient(145deg,rgba(244,235,218,0.96),rgba(223,211,190,0.92))] ring-1 ring-inset ring-[#b9934f]/25'
                              : 'border-[#d8c9aa]/55 bg-[linear-gradient(145deg,rgba(250,247,241,0.92),rgba(226,217,202,0.78))] hover:-translate-y-0.5 hover:border-[#b9934f]/70'
                          }`}
                        >
                          <div className="relative aspect-[4/3] overflow-hidden rounded-[11px] bg-[#d9d1c5]">
                            <img
                              src={scene.thumbnail}
                              alt={sceneTitle}
                              className="h-full w-full object-cover brightness-[0.96] contrast-[1.02] saturate-[0.94] transition-transform duration-700 group-hover:scale-[1.045]"
                            />
                            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(54,46,34,0.08)_100%)]" />
                            {isSelected && (
                              <span className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center border border-white/55 bg-[#8f867d]/56 text-white shadow-[0_8px_20px_rgba(142,132,122,0.18)] backdrop-blur">
                                <span aria-hidden="true" className="material-symbols-outlined text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }} data-icon="check" />
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between gap-3 px-1.5 pb-1 pt-3">
                            <p className="min-w-0 truncate font-serif text-[1.02rem] font-bold leading-tight text-[#2b241c]">
                              {sceneTitle}
                            </p>
                            <span
                              className={`h-2.5 w-2.5 shrink-0 rounded-full border transition-colors duration-200 ${
                                isSelected
                                  ? 'border-[#8d6f35] bg-[#b9934f] shadow-[0_0_0_3px_rgba(185,147,79,0.18)]'
                                  : 'border-[#cbb790]/70 bg-[#b8ad9f] shadow-[0_0_0_3px_rgba(255,255,255,0.38)]'
                              }`}
                              aria-hidden="true"
                            />
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </section>
              </div>
            </section>

          </aside>
        </div>
      </main>

      {renderStepNoticeToast()}

      <ImageLightbox
        image={lightboxImage}
        onClose={() => setLightboxImage(null)}
        closeLabel={closePreviewLabel}
      />
    </div>
  )
}

export default Upload
